// usePanelDragGrip — pointer-capture ドラッグの「掴み所 (グリップ)」ハンドラを提供する
// 共有 hook (docs/_wip/floating-panels-2026-08-01.md §2「フロートパネルも同じグリップ・
// 同じ drag 経路を通ること (2 系統作らない)」)。
//
// 背景: S1 の `ModernPanelDragShell` はグリップの pointer-capture メカニクス
// (pointerdown → 数 px 閾値判定 → `setPointerCapture` → pointermove → pointerup/cancel
// で確定/キャンセル、Escape キーでもキャンセル) を自前で実装していた。S2 でフロートパネル
// 用の `FloatShell` にも **同じグリップ挙動** が要る。「着地先の判定 (dock か float か、
// movePanel/floatPanel のどちらを適用するか)」は既に呼び出し側 (ModernLayout) の 3
// コールバック (`onPointerDragStart`/`onPointerDragMove`/`onPointerDragEnd`) に一元化されて
// いるが、**そのコールバックを呼ぶための pointer-capture 配管自体が 2 箇所に重複していると
// 「2 系統」の実質を残す**ため、本 hook へ抽出して `ModernPanelDragShell` と `FloatShell`
// の両方から使う (= ドック内パネルとフロートパネルが文字通り同じイベント処理コードパスを通る)。
//
// 見た目 (opacity-40 / 挿入線 / フロート追従表示) は呼び出し側コンポーネントの責務のまま
// (= この hook は「掴んで動かす」の配管だけを提供し、何を描くかには関与しない)。

import { useCallback, useEffect, useRef, useState } from 'react';

import type { FloatPoint } from './floatShellGeometry.js';
import { exceedsDragThreshold } from './panelDragGeometry.js';

export interface UsePanelDragGripOptions {
  /**
   * ドラッグ開始 (= 閾値を超えて動いた瞬間、一度だけ)。`grabOffset` は pointerdown 時点で
   * `measureGrabOffset` が計測した「掴んだ点 − 対象要素左上」の相対距離 (px、floating-panels
   * -2026-08-01.md §11「掴んだ位置を保持する」)。呼び出し側はこれをドラッグ中ずっと保持し、
   * `floatPositionFromPointer` へそのまま渡す。
   */
  readonly onDragStart: (grabOffset: FloatPoint) => void;
  /** ドラッグ中の pointermove (= client 座標を都度渡す)。 */
  readonly onDragMove: (clientX: number, clientY: number) => void;
  /**
   * ドラッグ終了。`committed=true` (pointerup) なら親が現在の着地判定を確定させ、
   * `false` (pointercancel / Escape) なら何もせず状態だけ戻す。
   */
  readonly onDragEnd: (committed: boolean) => void;
  /**
   * pointerdown 時点で「掴んだ点 (`e.clientX/Y`) − 対象要素の rect 左上」を計測する
   * (§11「offset は pointerdown 時点で `pointer − 対象要素の rect 左上` として計測する」)。
   * 対象要素の選び方は呼び出し側に委ねる (= フロート中パネルは `FloatShell` root の rect、
   * ドック内パネル (detach) は `[data-panel-id]` 要素の rect が基準、と挙動が異なるため
   * hook 側では決め打ちしない)。pointerdown の瞬間 (= まだ動く前) に一度だけ呼ばれる。
   */
  readonly measureGrabOffset: (e: React.PointerEvent<HTMLElement>) => FloatPoint;
}

export interface PanelDragGripHandlers {
  /** 現在 pointer が down している間 true (= 閾値未満のクリックの間も含む)。 */
  readonly isPointerActive: boolean;
  readonly onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
  readonly onLostPointerCapture: (e: React.PointerEvent<HTMLElement>) => void;
}

/**
 * グリップ要素にそのまま spread できる pointer ハンドラ一式を返す。呼び出し側は
 * `<div {...grip} />` のように配線するだけでよい (= `ModernPanelDragShell` / `FloatShell`
 * どちらも同じ形で使う)。
 */
export const usePanelDragGrip = ({
  onDragStart,
  onDragMove,
  onDragEnd,
  measureGrabOffset,
}: UsePanelDragGripOptions): PanelDragGripHandlers => {
  // pointerdown から pointerup/cancel までのローカル状態。`startRef` は「掴んだ開始座標 +
  // pointerId + 実測 grab-offset」、`draggingRef` は「既に閾値を超えてドラッグ開始を親へ
  // 通知済みか」(= onDragStart を 1 回だけ呼ぶためのガード)。grabOffset は pointerdown の
  // 瞬間に measureGrabOffset で一度だけ計測し (§11「offset は pointerdown 時点で計測する」)、
  // 閾値超え (= 実際にドラッグ開始とみなす瞬間) まで保持しておいて onDragStart へ渡す。
  const startRef = useRef<{
    readonly x: number;
    readonly y: number;
    readonly pointerId: number;
    readonly grabOffset: FloatPoint;
  } | null>(null);
  const draggingRef = useRef<boolean>(false);
  // window keydown (Escape) を「ドラッグ開始済みの間だけ」listen するための state
  // (= ref だけだと effect の再評価契機が無いので、開始/終了を state で明示する)。
  const [isPointerActive, setIsPointerActive] = useState(false);

  const endDrag = useCallback(
    (committed: boolean, el: HTMLElement, pointerId: number): void => {
      if (el.hasPointerCapture(pointerId)) {
        el.releasePointerCapture(pointerId);
      }
      const wasDragging = draggingRef.current;
      startRef.current = null;
      draggingRef.current = false;
      setIsPointerActive(false);
      if (wasDragging) {
        onDragEnd(committed);
      }
    },
    [onDragEnd],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>): void => {
      // 右クリック (button=2、ペンタブの barrel button 含む) はドラッグ対象外 (= 右クリックの
      // 既定コンテキストメニューは従来通り出す、`preventDefault` しない = 挙動不変)。
      // 複数ポインタの同時ドラッグも防ぐ (`ArgbSliderPanel.handleDown` と同じ流儀)。
      if (e.button === 2 || !e.isPrimary) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      // バグ修正 (2026-08-01 実機報告「フロートウィンドウをドラッグしても動かないことがある」):
      // preventDefault を呼んでいなかったため、掴み所の面積を FloatShell 全体へ拡張した際に
      // (= 後述、パネル自身のヘッダ等) ブラウザの既定動作 (テキスト選択開始 / ネイティブ画像
      // drag-and-drop) が発火しうる状態だった。ネイティブ DnD が発動すると半透明の drag image
      // がポインタに追従し、本コンポーネントの pointer-capture ドラッグは並行して動いていても
      // 見た目上「掴んだのに反応が薄い/動かない」ように見える (= ユーザー報告と一致)。
      // setPointerCapture の直後に呼ぶ (先に capture を確保してから既定動作を止める順序、
      // capture 自体は preventDefault の影響を受けない)。ボタン等インタラクティブ要素は
      // 呼び出し側 (FloatShell) が closest() で弾いてからここへ委譲するので、
      // ボタンの click を巻き込むことはない。
      e.preventDefault();
      startRef.current = {
        x: e.clientX,
        y: e.clientY,
        pointerId: e.pointerId,
        grabOffset: measureGrabOffset(e),
      };
      draggingRef.current = false;
      setIsPointerActive(true);
    },
    [measureGrabOffset],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>): void => {
      const start = startRef.current;
      if (!start || start.pointerId !== e.pointerId) return;
      if (!draggingRef.current) {
        // 既定閾値 (DRAG_START_THRESHOLD_PX) を使う (= panelDragGeometry.ts の単一ソース)。
        if (!exceedsDragThreshold(start, { x: e.clientX, y: e.clientY })) return;
        draggingRef.current = true;
        onDragStart(start.grabOffset);
      }
      onDragMove(e.clientX, e.clientY);
    },
    [onDragStart, onDragMove],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLElement>): void => {
      const start = startRef.current;
      if (!start || start.pointerId !== e.pointerId) return;
      endDrag(true, e.currentTarget, e.pointerId);
    },
    [endDrag],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLElement>): void => {
      const start = startRef.current;
      if (!start || start.pointerId !== e.pointerId) return;
      endDrag(false, e.currentTarget, e.pointerId);
    },
    [endDrag],
  );

  // Escape でドラッグをキャンセルする (S1 スコープ)。ドラッグ開始済みの間だけ window に
  // 張る (= 未ドラッグ中の Escape を横取りしない、他のショートカット/モーダルと衝突しない)。
  useEffect(() => {
    if (!isPointerActive) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      const start = startRef.current;
      if (!start) return;
      // el 参照は state を持たないので、キャンセル時は capture 解放だけ試みる
      // (要素が既に unmount されていても releasePointerCapture 自体は no-op で安全)。
      startRef.current = null;
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      setIsPointerActive(false);
      if (wasDragging) onDragEnd(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPointerActive, onDragEnd]);

  return {
    isPointerActive,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture: onPointerCancel,
  };
};
